import React, { createContext, useCallback, useContext, useRef } from 'react';

type FlushFn = () => Promise<void>;

interface NotesFlushContextValue {
  /** Register flush (Notes mount) or unregister (Notes unmount). Called by NotesSection. */
  registerNotesFlush: (fn: FlushFn | null) => void;
  /** Get current flush; used by AppShell before section switch. Returns null when Notes not mounted. */
  getNotesFlush: () => FlushFn | null;
}

const NotesFlushContext = createContext<NotesFlushContextValue | undefined>(undefined);

export const NotesFlushProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const flushRef = useRef<FlushFn | null>(null);

  const registerNotesFlush = useCallback((fn: FlushFn | null) => {
    flushRef.current = fn;
  }, []);

  const getNotesFlush = useCallback(() => flushRef.current, []);

  const value: NotesFlushContextValue = React.useMemo(
    () => ({ registerNotesFlush, getNotesFlush }),
    [registerNotesFlush, getNotesFlush]
  );

  return (
    <NotesFlushContext.Provider value={value}>
      {children}
    </NotesFlushContext.Provider>
  );
};

export function useNotesFlush(): NotesFlushContextValue {
  const ctx = useContext(NotesFlushContext);
  if (!ctx) throw new Error('useNotesFlush must be used within NotesFlushProvider');
  return ctx;
}

export function useNotesFlushOptional(): NotesFlushContextValue | undefined {
  return useContext(NotesFlushContext);
}
